/**
 * BenefitsPage — Club de Beneficios.
 *
 * Conectada al backend real (`GET /api/v1/partner-benefits`).
 * Filtros: búsqueda, multi-select categorías, ordenar, sólo vigentes, sólo nuevos.
 */

import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@app/components/common/PageHeader';
import { EmptyState } from '@app/components/common/EmptyState';
import { StatusBadge } from '@app/components/common/StatusBadge';
import { usePartnerBenefits } from '@modules/partner-benefits/presentation/hooks/usePartnerBenefits';
import {
  PARTNER_BENEFIT_CATEGORY_LABELS,
  type PartnerBenefitCategory,
  type PartnerBenefitResponse,
} from '@modules/partner-benefits/application/dto/PartnerBenefitDTO';
import { formatDate } from '@shared/utils/formatters';
import { cn } from '@shared/utils/cn';
import {
  Search,
  SearchX,
  BadgePercent,
  ExternalLink,
  MapPin,
  Sparkles,
} from 'lucide-react';

type SortKey = 'recent' | 'name-asc' | 'expiring';

const CATEGORIES: { key: PartnerBenefitCategory; label: string }[] = (
  Object.keys(PARTNER_BENEFIT_CATEGORY_LABELS) as PartnerBenefitCategory[]
).map((k) => ({ key: k, label: PARTNER_BENEFIT_CATEGORY_LABELS[k] }));

export function BenefitsPage() {
  const [search, setSearch] = useState('');
  const [selectedCats, setSelectedCats] = useState<Set<PartnerBenefitCategory>>(new Set());
  const [sortBy, setSortBy] = useState<SortKey>('recent');
  const [onlyValid, setOnlyValid] = useState(true);
  const [onlyNew, setOnlyNew] = useState(false);
  const [openDetail, setOpenDetail] = useState<PartnerBenefitResponse | null>(null);

  // Pedimos todo al backend sin filtros; filtramos client-side para feedback inmediato.
  const { data: benefits = [], isLoading } = usePartnerBenefits();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = benefits.filter((b) => {
      if (!b.isActive) return false;
      if (onlyValid && b.validUntil && new Date(b.validUntil) < new Date()) return false;
      if (onlyNew && !b.isNew) return false;
      if (selectedCats.size > 0 && !selectedCats.has(b.category)) return false;
      if (q) {
        const hay =
          b.partnerName.toLowerCase().includes(q) ||
          b.shortDescription.toLowerCase().includes(q) ||
          (b.longDescription ?? '').toLowerCase().includes(q) ||
          PARTNER_BENEFIT_CATEGORY_LABELS[b.category].toLowerCase().includes(q);
        if (!hay) return false;
      }
      return true;
    });

    result = [...result].sort((a, b) => {
      if (sortBy === 'name-asc') return a.partnerName.localeCompare(b.partnerName, 'es');
      if (sortBy === 'expiring') {
        const av = a.validUntil ? new Date(a.validUntil).getTime() : Number.POSITIVE_INFINITY;
        const bv = b.validUntil ? new Date(b.validUntil).getTime() : Number.POSITIVE_INFINITY;
        return av - bv;
      }
      // recent: por createdAt desc
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return result;
  }, [benefits, search, selectedCats, sortBy, onlyValid, onlyNew]);

  const toggleCategory = (cat: PartnerBenefitCategory) => {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const resetFilters = () => {
    setSearch('');
    setSelectedCats(new Set());
    setOnlyValid(true);
    setOnlyNew(false);
    setSortBy('recent');
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Club de Beneficios"
        subtitle="Descubre todos los beneficios exclusivos para nuestros clientes"
      />

      {/* Filtros */}
      <section className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-xs sm:p-6">
        {/* Buscador */}
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar beneficios..."
            className="pl-9"
          />
        </div>

        {/* Categorías */}
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Categorías
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedCats(new Set())}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                selectedCats.size === 0
                  ? 'border-sidebar-primary/30 bg-sidebar-primary/15 text-sidebar-primary'
                  : 'border-border bg-card text-muted-foreground hover:text-foreground',
              )}
            >
              Todas
            </button>
            {CATEGORIES.map(({ key, label }) => {
              const active = selectedCats.has(key);
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => toggleCategory(key)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    active
                      ? 'border-sidebar-primary/30 bg-sidebar-primary/15 text-sidebar-primary'
                      : 'border-border bg-card text-muted-foreground hover:text-foreground',
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Orden + switches */}
        <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
            <div className="flex items-center gap-2">
              <Label htmlFor="sort" className="text-xs text-muted-foreground">
                Ordenar
              </Label>
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortKey)}>
                <SelectTrigger id="sort" className="h-9 w-40 text-xs sm:w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Más recientes</SelectItem>
                  <SelectItem value="name-asc">Nombre A-Z</SelectItem>
                  <SelectItem value="expiring">Vigencia próxima a vencer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Switch id="only-valid" checked={onlyValid} onCheckedChange={setOnlyValid} />
              <Label htmlFor="only-valid" className="text-xs">
                Sólo vigentes
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch id="only-new" checked={onlyNew} onCheckedChange={setOnlyNew} />
              <Label htmlFor="only-new" className="text-xs">
                Nuevos
              </Label>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Mostrando <span className="font-semibold text-foreground">{filtered.length}</span>{' '}
            de <span className="font-semibold text-foreground">{benefits.length}</span>
          </p>
        </div>
      </section>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-48 w-full rounded-2xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={SearchX}
          title="No encontramos beneficios"
          description="Probá ajustar los filtros o desactivar 'Sólo vigentes' para ver el catálogo completo."
          action={
            <Button variant="outline" className="rounded-full" onClick={resetFilters}>
              Limpiar filtros
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((b) => (
            <BenefitCard key={b.id} benefit={b} onClick={() => setOpenDetail(b)} />
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <BenefitDetailDialog
        benefit={openDetail}
        open={!!openDetail}
        onOpenChange={(open) => !open && setOpenDetail(null)}
      />
    </div>
  );
}

function BenefitCard({
  benefit,
  onClick,
}: {
  benefit: PartnerBenefitResponse;
  onClick: () => void;
}) {
  const expiring = benefit.validUntil ? new Date(benefit.validUntil) : null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col items-stretch rounded-2xl border border-border bg-card p-5 text-left shadow-xs transition-all hover:-translate-y-0.5 hover:border-foreground/15 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-base font-semibold text-foreground">{benefit.partnerName}</p>
          <p className="text-xs text-muted-foreground">
            {PARTNER_BENEFIT_CATEGORY_LABELS[benefit.category]}
          </p>
        </div>
        {benefit.isNew && <StatusBadge variant="new">Nuevo</StatusBadge>}
      </div>

      <div className="mt-3 flex gap-3">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted">
          {benefit.logoUrl ? (
            <img
              src={benefit.logoUrl}
              alt={benefit.partnerName}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <BadgePercent
              className="h-7 w-7 text-muted-foreground"
              strokeWidth={1.5}
              aria-hidden="true"
            />
          )}
        </div>
        <p className="line-clamp-3 text-sm text-muted-foreground">{benefit.shortDescription}</p>
      </div>

      {benefit.discountText && (
        <p className="mt-3 inline-flex w-fit items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
          {benefit.discountText}
        </p>
      )}

      <div className="mt-4 flex items-center justify-between border-t border-border pt-3 text-xs">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
          {benefit.schedule ?? 'Todos los días'}
        </span>
        {expiring && (
          <span className="text-chart-4">Hasta {formatDate(expiring)}</span>
        )}
      </div>
    </button>
  );
}

function BenefitDetailDialog({
  benefit,
  open,
  onOpenChange,
}: {
  benefit: PartnerBenefitResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  if (!benefit) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted">
              {benefit.logoUrl ? (
                <img
                  src={benefit.logoUrl}
                  alt={benefit.partnerName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <BadgePercent
                  className="h-6 w-6 text-muted-foreground"
                  strokeWidth={1.5}
                  aria-hidden="true"
                />
              )}
            </div>
            <div>
              <DialogTitle className="text-xl font-bold">{benefit.partnerName}</DialogTitle>
              <DialogDescription>
                {PARTNER_BENEFIT_CATEGORY_LABELS[benefit.category]}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {benefit.discountText && (
            <div className="rounded-xl bg-primary/10 px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-primary">Descuento</p>
              <p className="text-lg font-bold text-primary">{benefit.discountText}</p>
            </div>
          )}

          <p className="text-sm text-foreground">
            {benefit.longDescription ?? benefit.shortDescription}
          </p>

          {benefit.howToRedeem && (
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground">
                <Sparkles className="h-4 w-4 text-sidebar-primary" aria-hidden="true" />
                Cómo canjear
              </p>
              <p className="text-sm text-muted-foreground">{benefit.howToRedeem}</p>
            </div>
          )}

          {benefit.locations && benefit.locations.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground">
                <MapPin className="h-4 w-4 text-sidebar-primary" aria-hidden="true" />
                Sucursales
              </p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {benefit.locations.map((l, i) => (
                  <li key={i}>
                    {l.address}, {l.city}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
            <p className="text-xs text-muted-foreground">
              {benefit.schedule ?? 'Todos los días'}
              {benefit.validUntil && <> · vigente hasta {formatDate(benefit.validUntil)}</>}
            </p>
            {benefit.website && (
              <Button asChild variant="outline" size="sm" className="rounded-full">
                <a href={benefit.website} target="_blank" rel="noopener noreferrer">
                  Visitar sitio
                  <ExternalLink className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
